/** Combo mode: close-dodge multiplier, 2 lives, aggressive spawns. */

export const COMBO_LIVES_MAX = 2;
export const COMBO_MULTIPLIER_MAX = 262144;
export const COMBO_TIMEOUT_MS = 2000;
/** Fixed spawn cadence (ms); not tied to score or multiplier. */
export const COMBO_SPAWN_EVERY_MS = 6667;
export const COMBO_BALLS_PER_SPAWN = 60;
export const COMBO_BALL_BASE_SPEED = 1.15;
/** Matches `.comboMultiplierHud` fill (white + warm glow). */
export const COMBO_MULT_HUD_COLOR = "#ffffff";
export const COMBO_MULT_HUD_GLOW = "rgba(255,200,80,0.85)";
const COMBO_POPUP_LIFE_MS = 780;

/** Gap beyond collision surface to count as a close dodge (px, scaled by layout). */
export function comboCloseDodgeGap(layoutScale: number): number {
    return Math.max(10 * layoutScale, 14);
}

type ObstacleLike = {
    x: number;
    y: number;
    r: number;
};

type TrackState = {
    wasClose: boolean;
    credited: boolean;
};

type ComboMultPopup = {
    x: number;
    y: number;
    multiplier: number;
    bornAt: number;
    tilt: number;
};

const trackMap = new WeakMap<object, TrackState>();
const comboPopups: ComboMultPopup[] = [];

let lives = COMBO_LIVES_MAX;
let multiplier = 1;
let comboDeadline = 0;
let comboLostFlashUntil = 0;
let comboBoostFlashUntil = 0;
let heartDimProgress = [0, 0];
let livesMaxOverride: number | null = null;
let timeoutMsOverride: number | null = null;

function uprightTilt(angle: number): number {
    let a = angle;
    while (a > Math.PI) a -= Math.PI * 2;
    while (a < -Math.PI) a += Math.PI * 2;
    if (a > Math.PI / 2) a -= Math.PI;
    if (a < -Math.PI / 2) a += Math.PI;
    return a;
}

export function resetComboState(opts?: { livesMax?: number; timeoutMs?: number }): void {
    livesMaxOverride =
        opts?.livesMax != null && Number.isFinite(opts.livesMax)
            ? Math.max(1, Math.floor(opts.livesMax))
            : null;
    timeoutMsOverride =
        opts?.timeoutMs != null && Number.isFinite(opts.timeoutMs)
            ? Math.max(100, Math.floor(opts.timeoutMs))
            : null;
    lives = livesMaxOverride ?? COMBO_LIVES_MAX;
    multiplier = 1;
    comboDeadline = 0;
    comboLostFlashUntil = 0;
    comboBoostFlashUntil = 0;
    heartDimProgress = Array.from({ length: lives }, () => 0);
    comboPopups.length = 0;
}

function comboTimeoutMs(): number {
    return timeoutMsOverride ?? COMBO_TIMEOUT_MS;
}

export function getComboLives(): number {
    return lives;
}

export function getComboMultiplier(): number {
    return multiplier;
}

export function getComboDeadline(): number {
    return comboDeadline;
}

export function getComboLostFlashUntil(): number {
    return comboLostFlashUntil;
}

export function getComboBoostFlashUntil(): number {
    return comboBoostFlashUntil;
}

export function getHeartDimProgress(index: number): number {
    return heartDimProgress[index] ?? 0;
}

function trackFor(o: object): TrackState {
    let st = trackMap.get(o);
    if (!st) {
        st = { wasClose: false, credited: false };
        trackMap.set(o, st);
    }
    return st;
}

function registerCloseDodge(now: number, x: number, y: number): void {
    multiplier = Math.min(COMBO_MULTIPLIER_MAX, multiplier * 2);
    comboDeadline = now + comboTimeoutMs();
    comboBoostFlashUntil = now + 420;
    comboPopups.push({
        x,
        y,
        multiplier,
        bornAt: now,
        tilt: uprightTilt(-0.42 + (Math.random() - 0.5) * 0.28),
    });
}

function loseCombo(now: number): void {
    if (multiplier <= 1) return;
    multiplier = 1;
    comboDeadline = 0;
    comboLostFlashUntil = now + 900;
}

/** True once this ball already awarded a close-dodge credit. */
export function isComboBallDodged(o: object): boolean {
    return trackMap.get(o)?.credited === true;
}

export function tickComboTimer(now: number): void {
    if (multiplier > 1 && comboDeadline > 0 && now >= comboDeadline) {
        loseCombo(now);
    }
}

export function tickComboHearts(dt: number): void {
    const step = dt * 2.8;
    for (let i = 0; i < heartDimProgress.length; i++) {
        const target = i >= lives ? 1 : 0;
        const cur = heartDimProgress[i] ?? 0;
        if (cur < target) heartDimProgress[i] = Math.min(1, cur + step);
        else if (cur > target) heartDimProgress[i] = Math.max(0, cur - step);
    }
}

export function updateComboNearDodges(
    obstacles: ObstacleLike[],
    mouseX: number,
    mouseY: number,
    cursorR: number,
    layoutScale: number,
    now: number,
): boolean {
    tickComboTimer(now);
    const closeGap = comboCloseDodgeGap(layoutScale);
    let boosted = false;

    for (const o of obstacles) {
        const st = trackFor(o as object);
        if (st.credited) continue;

        const dist = Math.hypot(mouseX - o.x, mouseY - o.y);
        const surfaceGap = dist - (o.r + cursorR);

        if (surfaceGap >= 0 && surfaceGap <= closeGap) {
            st.wasClose = true;
        } else if (st.wasClose && surfaceGap > closeGap * 1.35) {
            st.wasClose = false;
            st.credited = true;
            registerCloseDodge(now, o.x, o.y);
            boosted = true;
        }
    }

    return boosted;
}

function pruneComboPopups(now: number): void {
    for (let i = comboPopups.length - 1; i >= 0; i--) {
        if (now - comboPopups[i]!.bornAt >= COMBO_POPUP_LIFE_MS) comboPopups.splice(i, 1);
    }
}

/** Floating tilted "x2" / "x4" … at the dodge site (HUD multiplier colors). */
export function drawComboMultiplierPopups(
    ctx: CanvasRenderingContext2D,
    now: number,
    layoutScale: number,
): void {
    pruneComboPopups(now);
    if (comboPopups.length === 0) return;
    const ls = layoutScale;
    for (const p of comboPopups) {
        const age = now - p.bornAt;
        if (age < 0 || age >= COMBO_POPUP_LIFE_MS) continue;
        const u = age / COMBO_POPUP_LIFE_MS;
        const fade = u < 0.12 ? u / 0.12 : 1 - (u - 0.12) / 0.88;
        const lift = (6 + 16 * u) * ls;
        const label = `x${p.multiplier}`;
        // Shrink as digit count grows so long multipliers stay readable and out of the way.
        const digitScale = Math.max(0.52, 1 - Math.max(0, label.length - 3) * 0.11);
        const fontPx = Math.max(10, Math.round(14 * ls * digitScale));
        ctx.save();
        ctx.translate(p.x, p.y - lift);
        ctx.rotate(p.tilt);
        ctx.globalAlpha = Math.max(0, Math.min(1, fade));
        ctx.font = `800 ${fontPx}px "Segoe UI", system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.lineWidth = Math.max(1.5, 2.2 * ls * digitScale);
        ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.strokeText(label, 0, 0);
        ctx.shadowColor = COMBO_MULT_HUD_GLOW;
        ctx.shadowBlur = 10 * ls;
        ctx.fillStyle = COMBO_MULT_HUD_COLOR;
        ctx.fillText(label, 0, 0);
        ctx.shadowBlur = 18 * ls;
        ctx.shadowColor = "rgba(255,120,40,0.55)";
        ctx.fillText(label, 0, 0);
        ctx.restore();
    }
}

/** Returns true when the run should end (no lives left). */
export function comboTakeDamage(now: number): boolean {
    if (lives <= 0) return true;

    lives -= 1;
    return lives <= 0;
}

export function formatComboMultiplier(): string {
    return `x${multiplier}`;
}

/** Keep combo deadlines aligned when gameplay time is frozen (pause / resume countdown). */
export function shiftComboTimers(ms: number): void {
    if (ms <= 0) return;
    if (comboDeadline > 0) comboDeadline += ms;
    if (comboLostFlashUntil > 0) comboLostFlashUntil += ms;
    if (comboBoostFlashUntil > 0) comboBoostFlashUntil += ms;
    for (const p of comboPopups) p.bornAt += ms;
}
